<template>
  <div class="algebra-kit-component">
    <div class="algebra-kit-content">
      <inline-loader v-if="!showEmptyPlaceHolder && isLoading" />
      <div
        class="sls-algebra-kit"
        :data-show-feedback="showFeedback"
        :data-has-randomised="hasRandomised"
        :data-spec="!isEmpty(legacySpec) ? legacySpec : undefined"
      >
        <akit-interaction
          v-if="!showEmptyPlaceHolder && isScorable"
          :session-id="sessionId"
          :ref-id="refId"
          :review-mode="reviewMode"
          :solution-mode="solutionMode"
          :assessment-mode="assessmentMode"
          :show-hint="!assessmentMode"
        />
        <akit-exercise
          v-else-if="!showEmptyPlaceHolder && !isScorable"
          :session-id="sessionId"
          :ref-id="refId"
          :persist="false"
        />
        <empty-placeholder v-else>
          <template #icon>
            <EmptyError />
          </template>
          <template #default>
            {{ t("algebra_kit_component_error_placeholder_message") }}
          </template>
        </empty-placeholder>
      </div>
    </div>

    <div v-if="showRandomisedBanner" class="randomised-banner">
      <svg-icon name="Info24" kind="action" />
      <span :data-text="randomisedMessage" />
    </div>

    <attachment-placeholder v-if="showActionTools">
      <template #action-tools>
        <button class="edit" type="button">
          <svg-icon name="Pencil24" kind="action" />
        </button>
        <button class="trash" type="button">
          <svg-icon name="Trash24" kind="action" />
        </button>
      </template>
    </attachment-placeholder>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import appConfigs from "@configs/app";
import EmptyError from "@images/placeholder/empty-states/EmptyError.svg?component";
import { cookieManager } from "@utils/cookie.manager";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import { EVENT, INPUT_MODE } from "@plugins/richtext/plugins/algebra-kit/AlgebraKit.constant";
import { isDefined, isEmpty } from "@utils/var.check";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import AttachmentPlaceholder from "@plugins/common/components/AttachmentPlaceholder.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import { useVm } from "@utils/vm.ts";

const props = withDefaults(
  defineProps<{
    showActionTools?: boolean;
    sessionId: string;
    refId: string;
    reviewMode?: boolean;
    solutionMode?: boolean;
    assessmentMode?: boolean;
    showFeedback: boolean;
    hasRandomised: boolean;
    isScorable?: boolean;
    hideRandomisedBanner?: boolean;
    legacySpec: string | null;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_parentComponent?: object;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_isDynamicallyMounted?: boolean;
  }>(),
  {
    showActionTools: false,
    reviewMode: false,
    solutionMode: false,
    assessmentMode: false,
    isScorable: true,
    hideRandomisedBanner: false,
    dynamicMountChildMixin_parentComponent: () => ({}),
    dynamicMountChildMixin_isDynamicallyMounted: false,
  },
);

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const { t } = useI18n();
const { vm } = useVm();
const isLoading = ref(true);

const showEmptyPlaceHolder = computed(() => !isDefined(props.sessionId));
const showRandomisedBanner = computed(
  () => props.hasRandomised && !props.hideRandomisedBanner && !showEmptyPlaceHolder.value,
);
const randomisedMessage = computed(() => t("algebra_kit_randomised_message"));

interface AkitEventData {
  inputMode?: string;
}
interface AkitEvent {
  event: string;
  data?: AkitEventData;
  refId: string;
  sessionId: string;
}

function bindAkListener() {
  const akit = (
    window as unknown as {
      AlgebraKIT?: { addExerciseListener: (scope: string, cb: (e: AkitEvent) => void) => void };
    }
  ).AlgebraKIT;

  akit?.addExerciseListener("all", (akitEvent) => {
    const akitInteraction = document.querySelector(
      `akit-interaction[ref-id="${akitEvent.refId}"][session-id="${akitEvent.sessionId}"]`,
    );

    if (akitEvent.event === EVENT.TOGGLE_HANDWRITING) {
      if (akitEvent.data?.inputMode === INPUT_MODE.KEYBOARD) {
        vm.$wirisModal?.remove({ refId: akitEvent.refId });
      } else if (akitEvent.data?.inputMode === INPUT_MODE.HANDWRITING) {
        vm.$wirisModal?.show({
          akitElement: akitInteraction,
        });
      }
    }
  });

  // listen to events related to a session
  akit?.addExerciseListener(props.sessionId, (akitEvent) => {
    if (akitEvent.event === EVENT.INTERACTION_CREATED) {
      isLoading.value = false;
    }
  });
}

onMounted(() => {
  // Check if AlgebraKIT is fully initialized with addExerciseListener method
  const isAlgebraKitInitialized =
    "AlgebraKIT" in window &&
    (window as Record<string, unknown>).AlgebraKIT &&
    typeof (
      window as unknown as {
        AlgebraKIT?: { addExerciseListener?: (scope: string, cb: (e: AkitEvent) => void) => void };
      }
    ).AlgebraKIT?.addExerciseListener === "function";

  if (isAlgebraKitInitialized) {
    // AlgebraKit is already loaded, bind listener immediately
    nextTick(() => {
      bindAkListener();
    });
    return;
  }

  // Check if script is already in DOM (being loaded by another component instance)
  const existingScript = document.getElementById("algebrakit") as HTMLScriptElement | null;
  if (existingScript) {
    // Script exists but not yet fully loaded, wait for it
    existingScript.addEventListener("load", () => {
      bindAkListener();
    });
    return;
  }

  // Initialize here
  // Script doesn't exist, initialize config and load it
  (window as unknown as { AlgebraKIT?: unknown }).AlgebraKIT = {
    config: {
      proxy: { url: appConfigs.algebraKitProxy },
      secureProxy: {
        url: appConfigs.algebraKitProxy + "/secure",
        headers: {
          Authorization: `Bearer ${cookieManager.getCookie("jwt_token")}`,
        },
      },
      styles: {
        general: {
          "font-general": '"Noto Sans",Arial,sans-serif',
          "font-buttons": '"Noto Sans",Arial,sans-serif',
          "font-size-buttons": "small",
          "font-weight-buttons": "bold",
          "text-transform-buttons": "uppercase",
          "color-hint": "#1459cf",
          "color-error": "#86010D",
          "border-radius-buttons": "1em",
          "shadow-buttons": "none",
          "padding-buttons": "0.6em 1.5em",
          "height-form-controls": "40px",
          "border-radius-form-controls": "10px",
          "border-radius": "10px",
          "color-fg-buttons-default": "#1459CF",
          "color-bg-buttons-default": "transparent",
          "border-buttons-default": "1px solid #1459CF",
          "color-fg-buttons-default-hover": "white",
          "color-bg-buttons-default-hover": "#15499F",
          "border-buttons-default-hover": "1px solid #15499F",
          "color-fg-buttons-primary": "white",
          "color-bg-buttons-primary": "#1459CF",
          "border-buttons-primary": "1px solid #1459CF",
          "color-fg-buttons-primary-hover": "white",
          "color-bg-buttons-primary-hover": "#15499F",
          "border-buttons-primary-hover": "1px solid #15499F",
        },
        "formula-editor": {
          "color-primary": "#b6bdc5",
          "color-secondary": "#9da7b2",
          "color-sub-palette": "#0b233f",
          "color-tab-hover": "#546579",
          "color-palette-buttons-fg": "white",
          "color-palette-buttons-bg": "#0b233f",
          "border-palette-buttons": "1px solid white",
          "placeholder-color-main": "white",
          "placeholder-color-sub": "white",
        },
        multistep: {
          "color-worksheet-bg": "white",
          "border-worksheet": "1px solid #ccc",
          "color-buttons-fg": "#0b233f",
          "color-buttons-fg-hover": "white",
          "color-buttons-bg": "white",
          "color-buttons-bg-hover": "#4f5a6d",
          "border-buttons": "1px solid #0b233f",
          "border-buttons-hover": "none",
          "border-radius-buttons": "1em",
          "color-hint-label": "var(--akit-color-hint)",
          "color-hint-main": "var(--akit-color-hint)",
          "color-hint-expr": "inherit",
          "color-hint-detail": "inherit",
          "color-fb-label": "var(--akit-color-error)",
          "color-fb-main": "var(--akit-color-error)",
          "color-fb-expr": "inherit",
          "color-fb-detail": "inherit",
          "color-buttons-fg-disabled": "white",
          "color-buttons-bg-disabled": "#cfd3d8",
          "border-buttons-disabled": "none",
        },
        derivation: {
          "color-step-bg": "#F5F7FC",
          "color-derivation-bg": "transparent",
          "shadow-step": "0 3px 5px 0 rgb(0 0 0 / 15%)",
        },
        "exercise-editor": {
          "color-bg-settings-button": "#485A6D",
          "color-step-buttons-active": "#1459CF",
          "color-bg-header": "white",
          "color-bg-balloon-title": "#C7CDD3",
          "color-fg-summary": "var(--akit-color-main-fg)",
          "color-bg-summary": "transparent",
          "border-collapsable-content": "none",
          "border-hr-step-buttons": "1px solid #dddddd",
          "border-bottom-summary": "1px solid #ddd",
          "padding-summary": "5px 0px",
          "color-bg-task-selector": "white",
          "color-fg-task-selector": "var(--akit-color-main-fg)",
        },
        interaction: {
          "padding-instruction": "1em",
          "border-radius-instruction-top": "10px",
          "border-radius-instruction-bottom": "10px",
          "border-instruction": "1px solid #ccc",
          "color-instruction-bg": "#efefef",
        },
      },
      behavior: {
        components: {
          authoring: {
            multistep: { initialExpression: true },
          },
        },
        general: {
          handwriting: { mode: "external", deferred: false },
        },
      },
    },
  } as unknown;

  const scriptEl = document.createElement("script");
  scriptEl.setAttribute("id", "algebrakit");
  scriptEl.setAttribute("src", appConfigs.algebrakitPluginJs);
  document.head.appendChild(scriptEl);
  scriptEl.onload = () => {
    bindAkListener();
  };
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.algebra-kit-component {
  .attachment-placeholder {
    margin-top: $spacing-03;
    background-color: $ui-02;
    border-radius: 10px;
  }

  .empty-placeholder {
    margin: $spacing-03 0;
    background-color: $ui-03;
  }

  .akit-multistep {
    .akit-multistep-feedback-container {
      width: calc(100% - 23px - 4px);
    }
  }

  .randomised-banner {
    @include carbon--type-style("label-01", true);
    position: relative;
    display: flex;
    align-items: center;
    padding: $spacing-03;
    margin-top: $spacing-03;
    gap: $spacing-02;
    background-color: $inverse-02;
    color: $ui-01;
    border-radius: 10px;

    .sls-icon {
      transform: scale(0.75);
      [stroke] {
        stroke: $ui-01;
      }

      [fill] {
        fill: $ui-01;
      }
    }

    span[data-text] {
      vertical-align: middle;
      &:before {
        content: attr(data-text);
        min-width: 1rem;
        min-height: 1rem;
      }
    }

    .answer-info & {
      margin: 0 5px;
    }
  }
}
#algebraKitComponent {
  display: block;
  margin-bottom: $spacing-05;
}
</style>
